/**
 * LAS FILAS DEL 3.1 (CQ2): de las líneas de los partes a los tratamientos del
 * cuaderno. Y, de paso, a dónde va lo que NO es un fitosanitario.
 *
 * UNA FILA = UN PARTE × UN PRODUCTO (PLAN §6, corrección del abogado del diablo).
 * Se juntan dos solo si TODO coincide: fecha, cultivo (especie y variedad),
 * aplicador, equipo, producto y nº, plaga, dosis por ha, eficacia y
 * observaciones. Entonces las parcelas se unen y las superficies se suman. Dos
 * sectores de una misma tarea con equipos distintos son DOS filas.
 *
 * A DÓNDE VA CADA LÍNEA:
 *   - excluida en el borrador: FUERA, sea lo que sea (CU9, decisión de Pablo del
 *     28-09-2026: «el documento es de la persona»; D16 queda supersedida). Sale del
 *     DOCUMENTO; el parte sigue igual. Si tenía indicio de fitosanitario, la guarda
 *     de salida lo AVISA (no bloquea).
 *     [Antes, 26-09: fuera SOLO si pasaba la LISTA BLANCA de D16 —un abono o un
 *     macroorganismo sin indicio de fitosanitario— y lo demás se quedaba y contaba en
 *     `noSePuedenQuitar`, que desde CU9 va siempre vacía.]
 *   - con nº de registro VÁLIDO (resolvedor único, `src/lib/registry`): al 3.1
 *     (en 2026, 19 de 74 líneas «other» y 2 de 16 «biological» tenían nº válido);
 *   - abono (categoría fertilizer) sin nº: al 7;
 *   - macroorganismo (Nesidiocoris, Swirski…) sin nº: al 3.1 bis como
 *     alternativa no química si hay asesor con ROPO; si no, a Observaciones (D20);
 *   - lo demás (sin nº y sin saber qué es): al 3.1 con el nº como hueco, para
 *     preguntarlo por producto.
 *
 * LAS EDICIONES DE LA PERSONA (CU9): lo que el borrador dice de una línea del 3.1
 * (`decisiones.ediciones`: dosis, cantidad total, producto y nº, plaga, superficie,
 * fecha, eficacia, aplicador, equipo, observaciones) va ENCIMA de lo que sale del
 * parte, tal cual, con origen «usuario», y quita el hueco de ese campo. La cantidad
 * total, sin dosis dada, se convierte en dosis/ha entre la superficie (la dada o la
 * del parte). Las líneas añadidas (`anadida`) pasan por el mismo camino. Nada de
 * esto cambia el parte: la guarda de salida no vuelve a calcular lo que decidió la
 * persona y solo AVISA (nº que no está en el MAPA, dosis distinta del parte…).
 */
import { buildRegistryIndex, resolveRegistration, type RegistrationResolution } from "@/lib/registry/resolveRegistration";
import { pickUnambiguous, searchRegistry, type RegistryItem } from "@/lib/registrySuggest";
import { normalizeRegistration } from "@/lib/productMatch";
import type { EntradaCuaderno, LineaEntrada, ParteEntrada, ProductoInventario } from "./entrada";
import { type ClaseLinea, type MotivoHueco, type Origen } from "./modeloOficial";
import { componerObservaciones, notaDeParte, textoTiempo } from "./observaciones";
import { dosisPorHa } from "./dosePerHa";
import { esAbono, esMacroorganismo, indiceNombresRegistro, type CampoEdicion, type EdicionLinea, type IndiceNombresRegistro } from "./borrador";
import type { PlanParcelas } from "./parcelOrder";
import type { ParcelasRef } from "./types";
import { num } from "./format";
import { especieOficial } from "./cultivos";

type Tratamiento31 = {
  parcelas: ParcelasRef;
  especie?: string | null;
  variedad?: string | null;
  fechas: { desde: string; hasta?: string | null };
  superficieHa?: number | null;
  problema?: string | null;
  aplicador?: number | null;
  equipo?: number | null;
  producto: { nombreComercial: string; sustanciaActiva?: string | null; numeroRegistro?: string | null };
  dosis?: { valor: number; unidad: "kg/ha" | "l/ha" } | null;
  eficacia?: "buena" | "regular" | "mala" | null;
  observaciones?: string | null;
};

/** Lo que el motor sabe de cada fila del 3.1 además de la fila (para huecos y revisión). */
export interface MetaFila31 {
  lineas: string[];
  partes: string[];
  origenes: Partial<Record<keyof Tratamiento31 | "producto.numeroRegistro" | "producto.sustanciaActiva", Origen>>;
  huecos: { campo: string; motivo: MotivoHueco }[];
  /** Resolución del nº (avisos: nº de otro producto, no vigente…). */
  registro: RegistrationResolution;
  /** Propuesta del catálogo cuando falta el nº (COSTAR «554» → 22060). */
  propuestaRegistro: { num: string; nombre: string } | null;
  /** CU9: campos que la persona decidió en el documento (la guarda no los recalcula: avisa). */
  usuario?: CampoEdicion[];
  /** CU9: la fila tiene alguna línea que no viene de ningún parte (añadida por la persona). */
  anadida?: boolean;
}

export interface Filas31 {
  filas: Tratamiento31[];
  meta: MetaFila31[];
  bis: Record<string, unknown>[];
  fertilizacion: Record<string, unknown>[];
  /** D20 sin asesor: sueltas que no caben en ninguna fila del mismo parte (van a la nota del 3.1). */
  sueltasSueltas: string[];
  /** Líneas que se quedaron fuera y por qué (borrador, parte sin nave). */
  fuera: { linea: string; motivo: "excluida" | "sin_nave" }[];
  /** Líneas excluidas en el borrador que NO se pudieron quitar. Desde CU9 (28-09-2026) va siempre vacía: sale cualquiera. */
  noSePuedenQuitar: string[];
  clases: Record<string, ClaseLinea>;
}


/** «50000 units» → «50.000 ud.»: la cantidad de una suelta, en el español del cuaderno (D15). */
const UNIDAD_TEXTO: Record<string, string> = { units: "ud.", unit: "ud.", ud: "ud.", uds: "ud.", unidades: "ud.", pkg: "env.", l: "l", ml: "ml", kg: "kg", g: "g" };
export function cantidadTexto(cantidad: number | null | undefined, unidad: string | null | undefined): string | null {
  if (cantidad == null || !Number.isFinite(Number(cantidad)) || Number(cantidad) <= 0) return null;
  const u = UNIDAD_TEXTO[String(unidad ?? "").trim().toLowerCase()] ?? String(unidad ?? "").trim();
  return `${num(Number(cantidad), 3)} ${u}`.trim();
}


/** «Spinosad 48% [SC]» → «Spinosad 48% SC» (D15: corto). */
function sustanciaCorta(formulado: string | null | undefined): string | null {
  const s = String(formulado ?? "").replace(/[[\]]/g, " ").replace(/\s+/g, " ").trim();
  return s || null;
}

/**
 * @param categoriaProducto la categoría que tiene HOY el producto en el inventario
 *   (la de la línea va en `l.categoria`).
 * @param indiceNombres los nombres del Registro MAPA (`indiceNombresRegistro`); sin
 *   él (sin catálogo o MAPA apagado) no se mira el nombre.
 *
 * CU9 (28-09-2026): una línea excluida en el borrador SALE, sea lo que sea (antes,
 * solo si pasaba la lista blanca de D16). `categoriaProducto` e `indiceNombres` se
 * quedan en la firma por la edge, que la llama igual; ya no deciden nada aquí.
 */
export function clasificarLinea(
  l: LineaEntrada,
  registroValido: boolean,
  excluida: boolean,
  _categoriaProducto?: string | null,
  _indiceNombres?: IndiceNombresRegistro | null,
): ClaseLinea {
  if (excluida) return "excluida";
  if (registroValido) return "fitosanitario";
  if (esAbono(l.categoria)) return "abono";
  if (esMacroorganismo(l.nombre)) return "macroorganismo";
  return "fitosanitario";
}

export interface ContextoFilas {
  entrada: EntradaCuaderno;
  partes: ParteEntrada[];
  plan: PlanParcelas;
  ordenAplicador: Map<string, number>;
  ordenEquipo: Map<string, number>;
  /** Hay asesor con ROPO (D18/D20). */
  asesor: { nombre: string; ropo: string } | null;
  /** CU9: nombre normalizado (`claveNombre`) → id en el 1.2 / 1.3, para el aplicador o el equipo que dice la persona. */
  aplicadorPorNombre?: Map<string, string>;
  equipoPorNombre?: Map<string, string>;
}

/** Un nombre para casar sin mayúsculas, tildes ni espacios de más (el aplicador o el equipo que dice la persona). */
export const claveNombre = (s: string | null | undefined): string =>
  String(s ?? "").trim().toLocaleLowerCase("es").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ");

/** Las ediciones de una línea (por su id o, si es añadida, por la clave de la añadida). */
export function edicionDe(l: Pick<LineaEntrada, "id" | "claveEdicion">, ediciones: Record<string, EdicionLinea> | null | undefined): EdicionLinea | null {
  if (!ediciones) return null;
  const k = l.claveEdicion ?? l.id;
  return ediciones[k] ?? ediciones[String(k).toLowerCase()] ?? null;
}

const areaDeParte = (p: ParteEntrada, ctx: ContextoFilas): number | null => {
  const a = Number(p.areaM2);
  if (Number.isFinite(a) && a > 0) return a;
  const s = ctx.entrada.sectores.find((x) => x.id === p.blockId);
  const b = Number(s?.areaM2);
  return Number.isFinite(b) && b > 0 ? b : null;
};

export function construirFilas31(ctx: ContextoFilas): Filas31 {
  const { entrada, partes, plan } = ctx;
  const catalogo: RegistryItem[] = entrada.catalogo ?? [];
  const indice = catalogo.length ? buildRegistryIndex(catalogo) : null;
  const porNum = new Map(catalogo.map((it) => [normalizeRegistration(it.num), it]));
  const inventario = new Map<string, ProductoInventario>((entrada.inventario ?? []).map((i) => [i.id, i]));
  const partePorId = new Map(partes.map((p) => [p.id, p]));
  const excluidas = entrada.decisiones?.excluidas ?? {};
  const ediciones = entrada.decisiones?.ediciones ?? null;
  // Los nombres del MAPA, una vez por cuaderno (la edge los usa para decir qué es cada línea).
  const nombresMapa = entrada.mapaValidation === false ? null : indiceNombresRegistro(catalogo);

  /* D17 + CU2: «Buena» confirmada en bloque vale para los partes SIN eficacia guardados
     antes de la confirmación (M1); los de después, o sin hora de guardado, se preguntan. */
  const corteEficacia = entrada.decisiones?.eficaciaConfirmadaHasta ? Date.parse(entrada.decisiones.eficaciaConfirmadaHasta) : NaN;
  const eficaciaDe = (p: ParteEntrada): { valor: Tratamiento31["eficacia"]; origen: Origen | null } => {
    if (p.eficacia) return { valor: p.eficacia, origen: p.eficaciaFuente === "defecto" ? "defecto" : p.eficaciaFuente === "usuario" ? "usuario" : "dato" };
    const creado = p.creadoEn ? Date.parse(p.creadoEn) : NaN;
    if (Number.isFinite(corteEficacia) && Number.isFinite(creado) && creado <= corteEficacia) return { valor: "buena", origen: "usuario" };
    return { valor: null, origen: null };
  };

  const out: Filas31 = { filas: [], meta: [], bis: [], fertilizacion: [], sueltasSueltas: [], fuera: [], noSePuedenQuitar: [], clases: {} };

  type Candidata = { fila: Tratamiento31; meta: MetaFila31; clave: string; ordenes: number[]; areaM2: number | null };
  const candidatas: Candidata[] = [];
  const sueltasPorParte = new Map<string, string[]>();

  for (const l of entrada.lineas) {
    const p = partePorId.get(l.parteId);
    if (!p) continue; // parte de fuera del periodo o de otro titular
    const parcela = plan.parcelaDeParte(p);
    if (!parcela) {
      out.fuera.push({ linea: l.id, motivo: "sin_nave" });
      continue;
    }
    // CU2: la especie en castellano oficial («Pomodoro» → Tomate); lo no reconocido, tal cual.
    // F4 cultivos SIEX: con código, manda el código (igual que en el 2.1).
    const especie = especieOficial(parcela.especie, parcela.especieCodigo ?? null).nombre;
    const inv = l.productoId ? inventario.get(l.productoId) : undefined;
    const registro = resolveRegistration(
      { snapshot: l.registro, inventory: inv?.registro, productName: l.nombre, batch: l.lote },
      { catalog: indice, mapaValidation: entrada.mapaValidation },
    );
    const excluida = Object.prototype.hasOwnProperty.call(excluidas, l.id);
    const clase = clasificarLinea({ ...l, categoria: l.categoria ?? inv?.categoria }, registro.value != null, excluida, inv?.categoria, nombresMapa);
    out.clases[l.id] = clase;
    // CU9: lo excluido SALE del documento (sea lo que sea); el parte sigue igual.
    if (clase === "excluida") {
      out.fuera.push({ linea: l.id, motivo: "excluida" });
      continue;
    }

    const areaM2Parte = areaDeParte(p, ctx);
    const dosis = dosisPorHa({
      cantidad: l.cantidad,
      unidad: l.unidad,
      dosisValor: l.dosisValor,
      dosisUnidad: l.dosisUnidad,
      agua: l.agua,
      aguaUnidad: l.aguaUnidad,
      unidadStock: inv?.unidad ?? l.unidad,
      areaM2: areaM2Parte,
    });
    const superficieParteHa = areaM2Parte != null ? areaM2Parte / 10000 : null;
    const superficieHa = superficieParteHa;
    const areaM2 = areaM2Parte;

    if (clase === "abono") {
      out.fertilizacion.push({
        fechas: { desde: p.fecha },
        parcelas: [parcela.orden],
        especie,
        variedad: parcela.variedad,
        superficieHa,
        material: l.nombre,
      });
      continue;
    }

    if (clase === "macroorganismo") {
      const cantidadTxt = cantidadTexto(l.cantidad, l.unidad);
      if (ctx.asesor) {
        out.bis.push({
          especie,
          variedad: parcela.variedad,
          parcelas: [parcela.orden],
          superficieCultivadaHa: parcela.areaM2 != null ? parcela.areaM2 / 10000 : null,
          superficieTratadaHa: superficieHa,
          plaga: l.plaga ?? null,
          noQuimica: { tipoMedida: `Suelta de ${l.nombre.trim()}`, intensidad: cantidadTxt, fecha: p.fecha },
          eficacia: eficaciaDe(p).valor ?? null,
        });
      } else {
        const t = `Suelta de ${l.nombre.trim()}${cantidadTxt ? `, ${cantidadTxt}` : ""}`;
        const lista = sueltasPorParte.get(p.id) ?? [];
        lista.push(t);
        sueltasPorParte.set(p.id, lista);
      }
      continue;
    }

    // ── Fitosanitario: una candidata a fila del 3.1 ──
    // CU9: lo que la persona decidió de esta línea en el documento (o null).
    const ed = edicionDe(l, ediciones);
    const usuario: CampoEdicion[] = ed ? (Object.keys(ed) as CampoEdicion[]) : [];
    const huecos: MetaFila31["huecos"] = [];
    const origenes: MetaFila31["origenes"] = { fechas: "dato", parcelas: "calculo", producto: "dato" };

    // Producto y nº: los de la persona, tal cual; si no, los del parte por el resolvedor.
    const productoUsuario = ed?.producto ?? null;
    const nombreImpreso = (productoUsuario?.nombre ?? l.nombre).trim();
    const numeroImpreso = productoUsuario ? productoUsuario.numero?.trim() || null : registro.value;
    let propuestaRegistro: MetaFila31["propuestaRegistro"] = null;
    if (productoUsuario) {
      origenes.producto = "usuario";
      if (numeroImpreso) origenes["producto.numeroRegistro"] = "usuario";
      else {
        huecos.push({ campo: "numeroRegistro", motivo: "falta" });
        if (catalogo.length) {
          const pick = pickUnambiguous(searchRegistry(catalogo, nombreImpreso), nombreImpreso);
          if (pick?.kind === "one") propuestaRegistro = { num: pick.item.num, nombre: pick.item.nombre };
        }
      }
    } else if (registro.value) {
      origenes["producto.numeroRegistro"] = "dato";
      /* L7 (D23): con el MAPA encendido pero SIN catálogo (no llegó), el nº se imprime
         tal cual se escribió y queda como hueco «sin comprobar» en la hoja de revisión:
         se deja descargar, pero nadie lo toma por comprobado. */
      if (entrada.mapaValidation !== false && !indice) huecos.push({ campo: "numeroRegistro", motivo: "registro_sin_comprobar" });
    } else {
      huecos.push({ campo: "numeroRegistro", motivo: registro.warnings.some((w) => w.kind !== "missing") ? "registro_no_valido" : "falta" });
      if (catalogo.length) {
        const pick = pickUnambiguous(searchRegistry(catalogo, l.nombre), l.nombre);
        if (pick?.kind === "one") propuestaRegistro = { num: pick.item.num, nombre: pick.item.nombre };
      }
    }
    const ficha = numeroImpreso ? porNum.get(normalizeRegistration(numeroImpreso)) : undefined;
    const sustancia = sustanciaCorta(ficha?.formulado);
    if (sustancia) origenes["producto.sustanciaActiva"] = "dato";

    if (!especie) huecos.push({ campo: "especie", motivo: "falta" });
    else origenes.especie = "dato";

    // Superficie: la de la persona (ha), tal cual; si no, la del parte.
    const superficieFila = ed?.superficie ?? superficieHa;
    const areaFila = ed?.superficie != null ? ed.superficie * 10000 : areaM2;
    if (ed?.superficie != null) origenes.superficieHa = "usuario";
    else if (superficieFila == null) huecos.push({ campo: "superficieHa", motivo: "sin_superficie" });
    else origenes.superficieHa = "calculo";

    const problema = (ed?.plaga ?? l.plaga)?.trim() || null;
    if (ed?.plaga) origenes.problema = "usuario";
    else if (!problema) huecos.push({ campo: "problema", motivo: "falta" });
    else origenes.problema = "dato";

    // Aplicador y equipo: el nombre que dice la persona, numerado en el 1.2 / 1.3.
    let aplicador = p.aplicador ? ctx.ordenAplicador.get(p.aplicador.id) ?? null : null;
    let aplicadorDe: Origen | null = aplicador == null ? null : p.deducido?.aplicador ? "deducido" : "dato";
    if (ed?.aplicador) {
      const id = ctx.aplicadorPorNombre?.get(claveNombre(ed.aplicador));
      aplicador = id ? ctx.ordenAplicador.get(id) ?? null : null;
      aplicadorDe = aplicador == null ? null : "usuario";
    }
    // CU2: lo que el parte no guardó y se dedujo sale «deducido» (confírmalo), no «dato».
    if (aplicador == null) huecos.push({ campo: "aplicador", motivo: "falta" });
    else if (aplicadorDe) origenes.aplicador = aplicadorDe;
    let equipo = p.equipo ? ctx.ordenEquipo.get(p.equipo.id) ?? null : null;
    let equipoDe: Origen | null = equipo == null ? null : p.deducido?.equipo ? "deducido" : "dato";
    if (ed?.equipo) {
      const id = ctx.equipoPorNombre?.get(claveNombre(ed.equipo));
      equipo = id ? ctx.ordenEquipo.get(id) ?? null : null;
      equipoDe = equipo == null ? null : "usuario";
    }
    if (equipo == null) huecos.push({ campo: "equipo", motivo: "falta" });
    else if (equipoDe) origenes.equipo = equipoDe;

    // Dosis: la de la persona tal cual; o su cantidad total entre la superficie; o la del parte.
    let dosisFila: Tratamiento31["dosis"] = dosis.ok ? { valor: dosis.valor, unidad: dosis.unidad } : null;
    if (ed?.dosis) {
      dosisFila = { valor: ed.dosis.valor, unidad: ed.dosis.unidad };
      origenes.dosis = "usuario";
    } else if (ed?.cantidad) {
      dosisFila = superficieFila ? { valor: Math.round((ed.cantidad.valor / superficieFila) * 1000) / 1000, unidad: ed.cantidad.unidad === "kg" ? "kg/ha" : "l/ha" } : null;
      if (dosisFila) origenes.dosis = "usuario";
      else huecos.push({ campo: "dosis", motivo: "sin_superficie" });
    } else if (dosis.ok) origenes.dosis = "calculo";
    else if ("motivo" in dosis) huecos.push({ campo: "dosis", motivo: dosis.motivo });

    const efParte = eficaciaDe(p);
    const eficacia = ed?.eficacia ? { valor: ed.eficacia, origen: "usuario" as Origen } : efParte;
    if (eficacia.valor && eficacia.origen) origenes.eficacia = eficacia.origen;
    else huecos.push({ campo: "eficacia", motivo: "falta" });

    let observaciones = componerObservaciones([textoTiempo(p.tiempo?.condicion), p.metodo, notaDeParte(p.notas)]);
    if (ed?.observaciones != null) {
      observaciones = ed.observaciones;
      origenes.observaciones = "usuario";
    } else if (observaciones) origenes.observaciones = p.tiempo?.fuente === "default" ? "defecto" : "dato";

    const fecha = ed?.fecha ?? p.fecha;
    if (ed?.fecha) origenes.fechas = "usuario";
    // Una línea añadida no viene de ningún parte: todo lo que tiene lo puso la persona.
    if (l.anadida) {
      origenes.fechas = "usuario";
      origenes.producto = "usuario";
    }

    const fila: Tratamiento31 = {
      parcelas: [parcela.orden],
      especie,
      variedad: parcela.variedad,
      fechas: { desde: fecha },
      superficieHa: superficieFila,
      problema,
      aplicador,
      equipo,
      producto: { nombreComercial: nombreImpreso, sustanciaActiva: sustancia, numeroRegistro: numeroImpreso },
      dosis: dosisFila,
      eficacia: eficacia.valor ?? null,
      observaciones: observaciones || null,
    };

    const clave = JSON.stringify([
      fecha,
      (especie ?? "").toLowerCase(),
      (parcela.variedad ?? "").toLowerCase(),
      aplicador,
      equipo,
      fila.producto.nombreComercial.toLowerCase(),
      numeroImpreso,
      (fila.problema ?? "").toLowerCase(),
      fila.dosis ? `${fila.dosis.valor} ${fila.dosis.unidad}` : "motivo" in dosis ? dosis.motivo : null,
      fila.eficacia,
      fila.observaciones ?? "",
      // CU9: una fila con algo decidido por la persona no se junta con una que sale del parte.
      usuario.length || l.anadida ? `u:${[...usuario].sort().join(",")}${l.anadida ? ":n" : ""}` : "",
    ]);
    candidatas.push({
      fila,
      meta: {
        lineas: [l.id],
        partes: [p.id],
        origenes,
        huecos,
        registro,
        propuestaRegistro,
        ...(usuario.length ? { usuario } : {}),
        ...(l.anadida ? { anadida: true } : {}),
      },
      clave,
      ordenes: [parcela.orden],
      areaM2: areaFila,
    });
  }

  // D20 sin asesor: la suelta va a las Observaciones de las filas del MISMO parte; si no hay, a la nota.
  for (const [parteId, sueltas] of sueltasPorParte) {
    const suyas = candidatas.filter((c) => c.meta.partes.includes(parteId));
    if (suyas.length === 0) {
      const p = partePorId.get(parteId)!;
      const parcela = plan.parcelaDeParte(p);
      for (const s of sueltas) out.sueltasSueltas.push(`${s} el ${p.fecha.split("-").reverse().join("/")}${parcela ? ` (parcela ${parcela.orden})` : ""}`);
      continue;
    }
    for (const c of suyas) {
      c.fila.observaciones = componerObservaciones([c.fila.observaciones, ...sueltas.map((s) => `${s} (alternativa no química)`)]) || null;
      c.meta.origenes.observaciones = c.meta.origenes.observaciones ?? "dato";
      c.clave = JSON.stringify([c.clave, c.fila.observaciones]);
    }
  }

  // Agrupar: solo si TODO coincide.
  const grupos = new Map<string, Candidata[]>();
  for (const c of candidatas) {
    const g = grupos.get(c.clave) ?? [];
    g.push(c);
    grupos.set(c.clave, g);
  }
  for (const g of grupos.values()) {
    const base = g[0];
    const ordenes = g.flatMap((c) => c.ordenes);
    const areas = g.map((c) => c.areaM2);
    const todasConArea = areas.every((a) => a != null);
    const fila: Tratamiento31 = {
      ...base.fila,
      parcelas: plan.idParcelas(ordenes, base.fila.fechas.desde),
      superficieHa: todasConArea ? Math.round((areas.reduce((s, a) => s + (a as number), 0) / 10000) * 10000) / 10000 : null,
    };
    const huecos = base.meta.huecos.filter((h) => h.campo !== "superficieHa");
    if (!todasConArea) huecos.push({ campo: "superficieHa", motivo: "sin_superficie" });
    out.filas.push(fila);
    const usuario = [...new Set(g.flatMap((c) => c.meta.usuario ?? []))];
    out.meta.push({
      ...base.meta,
      lineas: g.flatMap((c) => c.meta.lineas),
      partes: [...new Set(g.flatMap((c) => c.meta.partes))],
      huecos,
      ...(usuario.length ? { usuario } : {}),
      ...(g.some((c) => c.meta.anadida) ? { anadida: true } : {}),
    });
  }

  // Orden del 3.1: por fecha y, dentro del día, por primera parcela, producto, equipo y aplicador (estable).
  const idx = out.filas.map((_, i) => i);
  const primera = (f: Tratamiento31) => (f.parcelas === "TODAS" ? 0 : Math.min(...f.parcelas));
  idx.sort(
    (a, b) =>
      out.filas[a].fechas.desde.localeCompare(out.filas[b].fechas.desde) ||
      primera(out.filas[a]) - primera(out.filas[b]) ||
      out.filas[a].producto.nombreComercial.localeCompare(out.filas[b].producto.nombreComercial, "es") ||
      (out.filas[a].equipo ?? 0) - (out.filas[b].equipo ?? 0) ||
      (out.filas[a].aplicador ?? 0) - (out.filas[b].aplicador ?? 0),
  );
  out.filas = idx.map((i) => out.filas[i]);
  out.meta = idx.map((i) => out.meta[i]);
  return out;
}

/** ¿La fila del 3.1 tiene TODO lo que pide el modelo? (la medida de cobertura del plan, §0.3). */
export function filaCompleta(m: MetaFila31): boolean {
  return m.huecos.length === 0;
}

export type { Tratamiento31 };
